import { onAuthStateChanged } from "firebase/auth";

import { auth, signInWithGoogle } from "../utils/firebase.utils";
import { track } from "../utils/track";
import { getFeature, UNCLASSIFIED_ID } from "./registry";

/**
 * The one sign-in prompt, as a plain module rather than React state.
 *
 * Plain because two very different callers open it: components (a gated
 * button, a gated page) and the axios interceptor, which runs outside React
 * entirely when a request comes back 401. `SignInModal` subscribes and renders
 * whatever is current; nobody else holds prompt state.
 *
 * One prompt at a time (FRS FR25): a second request while one is open gets the
 * same promise, so three parallel 401s show one modal and all three resume —
 * or all three reject — together. The promise resolves `true` once signed in
 * and `false` when the user cancels; it never rejects. A failed popup leaves
 * the prompt open with an error code for the modal to word.
 */

// Only registry ids, or the reserved "unclassified" sentinel, ever reach
// track() or the sign-in call: the server's FeatureId validator accepts exactly
// those, and an id this bundle does not know (a server newer than the client)
// is reported as unclassified rather than dropped.
const known = (id) => (getFeature(id) || id === UNCLASSIFIED_ID ? id : UNCLASSIFIED_ID);

let current = null; // { featureId, trigger, status, error, promise, resolve, unlisten }
const subs = new Set();
const snapshot = () =>
  current && {
    featureId: current.featureId,
    trigger: current.trigger,
    status: current.status,
    error: current.error,
  };
const notify = () => {
  const s = snapshot();
  subs.forEach((fn) => fn(s));
};

/** Called with the current prompt (or null) on every change. Returns an unsubscribe. */
export const subscribe = (fn) => {
  subs.add(fn);
  return () => subs.delete(fn);
};

export const getPrompt = () => snapshot();

const finish = (ok, reason) => {
  const c = current;
  current = null;
  if (!c) return;
  c.unlisten?.();
  notify();
  if (!ok) track("sign_in_dismissed", { feature_id: c.featureId, reason });
  c.resolve(ok);
};

/**
 * Ask the user to sign in for `featureId`. Resolves `true` at once if they
 * already are.
 */
export function requestSignIn({ featureId: rawId, trigger = "click" } = {}) {
  const featureId = known(rawId);
  if (auth.currentUser) return Promise.resolve(true);
  if (current) return current.promise;

  let resolve;
  const promise = new Promise((r) => {
    resolve = r;
  });
  current = { featureId, trigger, status: "idle", error: null, promise, resolve };
  // Signed in from another tab, or from the header button, while the prompt
  // is open: that is a sign-in too, and the waiting action resumes. Listened
  // to only while a prompt is open, so importing this module (which every API
  // call does, through client.js) never touches Firebase.
  current.unlisten = onAuthStateChanged(auth, (user) => {
    if (user && current) finish(true);
  });
  track("sign_in_prompted", { feature_id: featureId, trigger });
  notify();
  return promise;
}

/** Dismiss the prompt; every waiting caller gets `false`. */
export const cancel = (reason = "cancelled") => finish(false, reason);

/** The modal's "Continue with Google". A second press while the popup is open is ignored. */
export async function startSignIn() {
  if (!current || current.status === "pending") return;
  const mine = current;
  mine.status = "pending";
  mine.error = null;
  notify();
  try {
    await signInWithGoogle({ featureId: mine.featureId });
    if (current === mine) finish(true);
  } catch (e) {
    if (current !== mine) return;
    // The modal stays open with something to say; only Cancel resolves false.
    mine.status = "idle";
    mine.error = e?.code ?? "error";
    notify();
  }
}
